import { useMutation } from '@apollo/client';
import { Form, Input, InputNumber, Modal, Select, Switch, notification } from 'antd';
import React, { useCallback } from 'react';
import { generateUuid } from '../../shared/utils';
import {
  ONBOARDING_STAGE_KEYS,
  OnboardingTaskAutoSources,
  OnboardingTaskRoles,
  PartnerOnboardingStages,
  PartnerTypes,
  TaskInputTypes,
  createOnboardingTaskTemplateMutation,
} from '../partner-onboarding/constants';

function CreateOnboardingTaskTemplateModal({ visible, onCancel, onSuccess }) {
  const [form] = Form.useForm();
  const [createTemplate, { loading }] = useMutation(createOnboardingTaskTemplateMutation);

  const handleFinish = useCallback(
    async (values) => {
      try {
        await createTemplate({
          variables: {
            template: {
              _id: generateUuid(),
              stageNumber: values.stageNumber,
              taskName: values.taskName,
              inputType: values.inputType,
              assignedRole: values.assignedRole,
              daysToComplete: values.daysToComplete ?? 7,
              order: values.order ?? 0,
              isActive: values.isActive ?? true,
              partnerTypeCodes: values.partnerTypeCodes || [],
              autoSource: values.autoSource || null,
              allowNotApplicable: values.allowNotApplicable ?? false,
            },
          },
        });
        notification.success({ message: 'Created', description: 'Task template created' });
        form.resetFields();
        onSuccess();
      } catch (err) {
        notification.error({ message: 'Error', description: err.message });
      }
    },
    [createTemplate, form, onSuccess],
  );

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      title="Add Task Template"
      onOk={() => form.submit()}
      okText="Create"
      confirmLoading={loading}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{ daysToComplete: 7, order: 0, isActive: true }}
        onValuesChange={(changed) => {
          if (changed.autoSource) form.setFieldsValue({ allowNotApplicable: false });
        }}
      >
        <Form.Item
          name="stageNumber"
          label="Stage"
          rules={[{ required: true, message: 'Stage is required' }]}
        >
          <Select placeholder="Select stage">
            {ONBOARDING_STAGE_KEYS.map((s) => (
              <Select.Option key={s} value={s}>
                {PartnerOnboardingStages[s]?.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="taskName"
          label="Task Name"
          rules={[{ required: true, message: 'Task name is required' }]}
        >
          <Input placeholder="Task name" />
        </Form.Item>
        <Form.Item
          name="inputType"
          label="Input Type"
          rules={[{ required: true, message: 'Input type is required' }]}
        >
          <Select placeholder="Select input type">
            {Object.values(TaskInputTypes).map((t) => (
              <Select.Option key={t.key} value={t.key}>
                {t.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="assignedRole"
          label="Assigned Role"
          rules={[{ required: true, message: 'Assigned role is required' }]}
        >
          <Select placeholder="Select role">
            {Object.values(OnboardingTaskRoles).map((r) => (
              <Select.Option key={r.key} value={r.key}>
                {r.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="daysToComplete" label="Days to Complete">
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="order" label="Order">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="isActive" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="partnerTypeCodes" label="Partner Types (optional)">
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="children"
            placeholder="All partner types"
          >
            {Object.entries(PartnerTypes).map(([code, name]) => (
              <Select.Option key={code} value={code}>
                {name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="autoSource"
          label="Auto-complete source (optional)"
          tooltip="If set, the system checks this task off automatically when the signal is true on the partner's contact."
        >
          <Select allowClear placeholder="None — completed manually">
            {Object.values(OnboardingTaskAutoSources).map((s) => (
              <Select.Option key={s.key} value={s.key}>
                {s.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) => prev.autoSource !== cur.autoSource}
        >
          {({ getFieldValue }) => {
            const auto = getFieldValue('autoSource');
            return (
              <Form.Item
                name="allowNotApplicable"
                label="Allow “N/A”"
                valuePropName="checked"
                tooltip={
                  auto
                    ? 'Not available for auto-completed tasks.'
                    : 'If on, staff can mark this task “N/A”. An N/A task counts as complete.'
                }
              >
                <Switch disabled={!!auto} />
              </Form.Item>
            );
          }}
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default CreateOnboardingTaskTemplateModal;
